import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Subject } from 'rxjs';
import {
  AppNotification,
  SocketService,
} from '../socket/socket.service';

export interface RealtimeRefreshEvent {
  target: 'report-details';
  notificationId: string;
  reportId?: string;
  reportName?: string;
  reportType?: string;
  status?: string;
  message?: string;
  failureReason?: string;
  reportReferenceId?: string;
  clientRequestId?: string;
  actionUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class RealtimeRefreshService {
  private readonly handledNotificationKeys = new Set<string>();

  private reportDetailsRefreshSubject =
    new Subject<RealtimeRefreshEvent>();

  private reportGenerationStatusSubject =
    new Subject<RealtimeRefreshEvent>();

  /**
   * Preserves the existing behavior: an event is emitted only when the
   * notification belongs to the report route currently displayed.
   */
  readonly reportDetailsRefresh$ =
    this.reportDetailsRefreshSubject.asObservable();

  /**
   * Emits terminal report-generation events even when the user is on the
   * create-report screen and the report route is not open yet.
   */
  readonly reportGenerationStatus$ =
    this.reportGenerationStatusSubject.asObservable();

  constructor(
    private router: Router,
    private socketService: SocketService
  ) {
    /*
     * Listen directly so report generation does not depend on a notification
     * UI component forwarding the socket event.
     */
    this.socketService.notifications$.subscribe((notification) => {
      this.handleNotification(notification);
    });
  }

  handleNotification(notification: AppNotification): void {
    if (
      !notification ||
      notification.category !== 'report'
    ) {
      return;
    }

    const notificationKey =
      notification.id ||
      [
        notification.reportId,
        notification.type,
        notification.status,
        notification.reportReferenceId,
      ]
        .filter(Boolean)
        .join(':');

    if (
      !notificationKey ||
      this.handledNotificationKeys.has(notificationKey)
    ) {
      return;
    }

    this.handledNotificationKeys.add(notificationKey);

    /* Keep the root-service deduplication set bounded. */
    if (this.handledNotificationKeys.size > 200) {
      const oldestKey =
        this.handledNotificationKeys.values().next().value;

      if (oldestKey) {
        this.handledNotificationKeys.delete(oldestKey);
      }
    }

    this.handleReportNotification(notification);
  }

  private handleReportNotification(notification: AppNotification): void {
    /*
     * A processing event only confirms that work started. The loader is
     * already processing, so only terminal events control its final state.
     */
    if (
      notification.status !== 'created' &&
      notification.status !== 'success' &&
      notification.status !== 'failed'
    ) {
      return;
    }

    const event = this.toRealtimeRefreshEvent(notification);

    /*
     * Always publish terminal generation status. This allows the create
     * screen and an active stepper to finish without depending on the
     * original HTTP response.
     */
    this.reportGenerationStatusSubject.next(event);

    /*
     * Preserve the old route-refresh behavior for a report that is already
     * open. Saved/live notifications for other routes do not refresh it.
     */
    if (
      !notification.actionUrl ||
      !this.isCurrentReportRoute(notification.actionUrl)
    ) {
      return;
    }

    this.reportDetailsRefreshSubject.next(event);
  }

  private toRealtimeRefreshEvent(
    notification: AppNotification
  ): RealtimeRefreshEvent {
    return {
      target: 'report-details',
      notificationId:
        notification.id ||
        `${notification.reportId || 'report'}:${notification.type}:${notification.status}`,
      reportId: notification.reportId,
      reportName: notification.reportName,
      reportType: notification.type,
      status: notification.status,
      message: notification.message,
      failureReason: notification.failureReason,
      reportReferenceId: notification.reportReferenceId,
      clientRequestId: notification.clientRequestId,
      actionUrl: notification.actionUrl,
    };
  }

  private isCurrentReportRoute(actionUrl: string): boolean {
    try {
      const currentUrlTree = this.router.parseUrl(this.router.url);
      const notificationUrlTree = this.router.parseUrl(actionUrl);

      const currentPath = this.getPrimaryRoutePath(currentUrlTree);
      const notificationPath = this.getPrimaryRoutePath(
        notificationUrlTree
      );

      if (currentPath !== notificationPath) {
        return false;
      }

      const currentReportId = currentUrlTree.queryParams.id;
      const notificationReportId =
        notificationUrlTree.queryParams.id;

      const currentReportType = currentUrlTree.queryParams.type;
      const notificationReportType =
        notificationUrlTree.queryParams.type;

      return (
        currentReportId === notificationReportId &&
        currentReportType === notificationReportType
      );
    } catch (error) {
      console.error(
        'Unable to compare notification route:',
        error
      );

      return false;
    }
  }

  private getPrimaryRoutePath(urlTree: UrlTree): string {
    const primarySegments =
      urlTree.root.children.primary?.segments || [];

    return primarySegments
      .map((segment) => segment.path)
      .join('/');
  }
}